import { Inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { ILink, LinkType } from '@epicuro-next/data/merchandise';
import { mapInternalLinkParams } from '@epicuro-next/data/product';
import {
  APP_PRIVATE_PATHS,
  APP_TOP_LEVEL_PATH,
} from '@epicuro-next/navigation/constants';
import {
  EnvironmentSettings,
  ENVIRONMENT_SETTINGS,
} from '@epicuro-next/platform/environment';
import { toBoolean } from '@epicuro-next/utilities';

const KEEP_QUERY_PARAMS = ['siteId'];

export interface PhoenixSearchParams {
  q: string;
  f: string;
  sortField: string;
  sortDirection: string;
}

export interface IAnchorProps {
  routerLink?: string[];
  queryParams?: { [key: string]: string };
  href?: string;
  external?: boolean;
}

@Injectable()
export class LandingLinkService {
  constructor(
    @Inject(ENVIRONMENT_SETTINGS)
    private environmentSettings: EnvironmentSettings,
  ) {}

  public getAnchorProps(link: ILink, siteId?: string): IAnchorProps {
    if (!link) return {};

    let multiSiteLinkProps: string[] = [];
    const multiSiteSupport = toBoolean(
      this.environmentSettings.multiSiteSupport,
    );
    if (multiSiteSupport && siteId) {
      multiSiteLinkProps = [
        APP_TOP_LEVEL_PATH.siteSelector.custom as string,
        siteId,
      ];
    }

    if (link.type === LinkType.PRODUCT_LIST) {
      return {
        routerLink: [
          '/',
          ...multiSiteLinkProps,
          APP_PRIVATE_PATHS.productList.path,
          link.linkParams.productList,
        ],
      };
    }

    if (link.type === LinkType.LOOK_BOOK) {
      return {
        routerLink: [
          '/',
          ...multiSiteLinkProps,
          APP_PRIVATE_PATHS.shopTheLook.path,
          link.linkParams.lookbookId,
          link.linkParams.productList ?? '',
        ],
      };
    }

    let routerLink;
    let queryParams;

    let href =
      link.type === LinkType.LINK
        ? // needs to be href || sref to avoid empty strings
          link.linkParams.href || link.linkParams.sref || undefined
        : undefined;

    // Apparently sref is internal link in console terms
    const external =
      link.type === LinkType.LINK ? !!link.linkParams.href : undefined;

    const origin = window.location.origin;
    if (
      link.type === LinkType.LINK &&
      (link.linkParams.href || link.linkParams.sref || '').includes(origin)
    ) {
      const parsed = this.parseUrl(
        link.linkParams.href || link.linkParams.sref || '',
      );

      routerLink = parsed.routerLink;
      queryParams = parsed.queryParams;
      href = undefined;
    }

    if (link.type === LinkType.SEARCH) {
      routerLink = ['/', ...multiSiteLinkProps, 'browse', 'search'];
      queryParams = {
        ...getCurrentQueryParams(),
        ...mapInternalLinkParams(link.params),
      };
    }

    return {
      href: href?.replace('/next/', '/'),
      routerLink,
      queryParams,
      external,
    };
  }

  public getLinkStyle(link: ILink) {
    if (!link.style) return undefined;
    const result: { [key: string]: string | undefined } = {};
    result.color = link.style.color;
    result['border-color'] = result.color;

    return result;
  }

  public anchorPropsToHref(routeProps: IAnchorProps) {
    if (routeProps.href) {
      return routeProps.href;
    }

    const url = new URL(
      routeProps.routerLink?.splice(1).join('/') ?? '',
      window.location.origin,
    );
    Object.keys(routeProps.queryParams ?? {}).forEach((key) => {
      url.searchParams.append(key, routeProps.queryParams?.[key] ?? '');
    });

    return url.href;
  }

  private parseUrl(href: string) {
    const origin = window.location.origin;

    const decodedURL = decodeURI(href);

    const url = decodedURL
      .replace('/next/', '/')
      .replace(origin, '')
      .replace(/^\/s\/[^\/]*/gim, '');

    const split = url.split('?');
    let queryParams: Params | undefined;

    if (split[1]) {
      queryParams = split[1]
        .split('&')
        .reduce((result: Params, value: string) => {
          const param: string[] = value.split('=');
          result[param[0]] = param[1].split('%26').join('&');

          return result;
        }, {});
    }

    // this regex /\(.+\)/ is needed to remove outlets from route. as of now it is not possible to navigate both regular/primary route and auxiliary
    return {
      routerLink: ['/', ...split[0].replace(/\(.+\)/, '').split('/')].filter(
        Boolean,
      ),
      queryParams,
    };
  }
}

function getCurrentQueryParams() {
  const result: { [key: string]: string } = {};
  const currentUrlSearchParams = new URLSearchParams(window.location.search);

  KEEP_QUERY_PARAMS.forEach((param) => {
    const value = currentUrlSearchParams.get(param);
    if (value) result[param] = value;
  });

  return result;
}
